export enum DualPage {
    SINGLE = 0,
    DUAL_COVER = 1,
    DUAL = 2,
}
export enum Zoom {
    PAGE = 0,
    WIDTH = 1,
    SCALE = 2,
}
export enum RequestType {
    LOAD_PDF = 0,
    SCALE = 1,
    SET_CURRENT_PAGE = 2,
    MAKE_DOC_THUMBNAIL = 3,
    SET_ORIENTATIONS = 5,
    WHEEL_EVENT = 6,
    THEME_BACKGROUNDS = 7,
    FIND = 8,
    LOAD_AND_THUMB = 9,
    COPY_SELECTION_TO_CLIPBOARD = 10,
    MAKE_QUICKVIEW = 12,
    KEY_EVENT = 13,
    TEXT_COPY_MODE_ENABLED = 14,
    CAN_SELECT_TEXT = 15,   // Note: this command will only work for a page that has already be loaded into view by pdfjs. 
                            // It returns ResponseType.CAN_SELECT_TEXT with an object { loaded, canSelectText }
                            // If loaded is false the page has not be loaded yet. Note that it take some time from the page is viewed until everything is actually loaded
    EMPTY_VIEWER = 16,
    SET_SPLIT_MARKS = 17,
    SET_DELETE_MARKS = 18,
    FIND_MATCH_RECTS = 19,  // Find all content-search matches and return their rects (for search-based redaction marking).
    GET_PAGE_RECTS = 20,    // Return page dimensions at scale 1 (markup space) for the given pages (for page redaction marking).
    GET_IMAGE_RECTS = 21,   // Return the page's image rects in markup space (for click-on-image redaction marking).
    SET_REDACTION_MODE = 22,    // Redaction v2 (#492): payload { enabled: boolean }. The viewer reloads the document drawn as its redacted
                                // copy will be: form fields drawn into the page and the PDF marks the copy leaves out hidden (redaction-v2-marks.ts).
    COUNT_PDF_LOSSES = 23,      // Redaction v2 (#492): count what a redacted copy of the loaded document loses. Returns ResponseType.PDF_LOSSES.
}
export interface ViewerRequest {
    requestType: RequestType;
    payload: any;
}
export interface DeleteMark {
    start: number;
    end: number;
}
export enum SrcType { URL, FILE_DATA }
export interface LoadPdfPayload {
    docKey: string;
    srcType: SrcType;
    src: any;
    fileSize: number;
    pageNumber: number;
    scalePayload: ScalePayload;
    centerX: number;
    centerY: number;
    orientations: { [pageNumber: number]: number };
    minScale: number;
    maxScale: number;
    password: string;
    throttleEventsTime: number; // sets the minimum time in msec between VIEW_CHANGED events
    redactionMode?: boolean;    // Redaction v2 (#492): load drawn as the redacted copy will be (see RequestType.SET_REDACTION_MODE)
    fileDropDisabled?: boolean; // #584: the member may not upload (read-only), so files dragged over the viewer get the no-drop
                                // cursor and a drop does nothing. Missing (an older app) leaves drops on, as before
}
export interface LoadAndThumbPayload {
    docKey: string;
    srcType: SrcType;
    src: any;
    pageNo: number;
    password: string;
    width: number;
    height: number;
}
export interface ScalePayload {
    scale: number;  // if scale is positive the value is used as is. If it is negative it is use as a factor multiplied to the previous scale
    zoom: Zoom;
    dualPage: DualPage;
}
export interface FindPayload {
    findCommand: string;    // 'find' for initial search, 'findagain' to find next occurence. set findPrevious to true to search reverse
    findParms: {
        query: string;
        phraseSearch: boolean;
        caseSensitive: boolean;
        entireWord: boolean;
        highlightAll: boolean;
        findPrevious: boolean;
    };
}
export interface ThumbnailPayload {
    width: number;          
    height: number;
    pageNumber: number;
    orientation: number;
    mimeType: string;       // 'image/jpeg' or 'image/png'
    jpegQuality?: number;   // between 0 and 1 if mimeType === 'image/jpeg'
}
export interface QuickViewPayload {
    markupKey: string;
    width: number;
    height: number;
    pageNumber: number;
    orientation: number;
    offsetX: number;        // Note that to crop the page the offsetX/Y shall be negative  
    offsetY: number;
    mimeType: string;       // 'image/jpeg' or 'image/png'
    scale: number;
    jpegQuality?: number;   // between 0 and 1 if mimeType === 'image/jpeg'
}
export interface WheelPayload {
    deltaY: number;
    zoomPointX: number;
    zoomPointY: number;
    zoom: boolean;
}

export enum ResponseType {
    FIND_UPDATE = 0,
    PROGRESS = 1,
    DOC_THUMBNAIL_MADE = 2,
    VIEW_CHANGED = 4,
    REJECT = 5,
    CLICK = 6,
    PASSWORD_PROTECTED = 7,
    LOAD_AND_THUMB_MADE = 8,
    PDF_LOADED = 9,             // TBD PRIO2 PN: When is this fired? (Would be nice with definition on all response types.
    FILES_DROPPED = 10,
    KEYUP = 11,
    VERSION = 12,
    QUICKVIEW_MADE = 14,
    SHOW_TEXT_COPY_BUTTON = 15,
    CAN_SELECT_TEXT = 16,   // returns { loaded, canSelectText } if loaded is false, it's because the page has not been loaded yet 
    PDF_LOAD_FAILED = 17,
    MATCH_RECTS = 18,       // Response to FIND_MATCH_RECTS.
    PAGE_RECTS = 19,        // Response to GET_PAGE_RECTS.
    IMAGE_RECTS = 20,       // Response to GET_IMAGE_RECTS.
    MOUSE_MOVE = 21,        // Throttled mouse position (ClickEvent payload, same client space as CLICK; x/y=-1 on mouse leave).
                            // For the parent's pending-redaction hover preview while its markup canvas is pointer-events:none.
    PDF_NOTES_OR_LINKS = 22,    // A shown page has the PDF's own notes or links (annotations other than form fields and popups).
                                // Sent at most once per loaded document; payload { pageNumber }. They respond only in Select (#486, #497).
    PDF_LOSSES = 23,        // Response to COUNT_PDF_LOSSES (PdfLosses payload).
}

// Redaction v2 (#492): what a redacted copy of the loaded document loses, for the notice before marking and the Apply dialog.
// Spec: docs/superpowers/specs/2026-10-18-redaction-v2-design.md, "Before marking: what this document will lose".
export interface PdfLosses {
    links: number;
    notes: number;          // the PDF's own sticky notes (Text marks)
    formFields: number;     // distinct form field names
    bookmarks: number;      // outline entries, nested ones included
    attachments: number;    // embedded files plus paperclip (FileAttachment) marks
    freeTextBoxes: number;  // free-text boxes saved without their own appearance (left out, see redaction-v2-marks.ts)
    mediaPlayers: number;   // sound, movie, screen and rich media marks
    failed: boolean;        // the document could not be counted; the app warns without counts
}

export interface ViewerResponse {
    responseType: ResponseType;
    latestRequest: string;
    viewerId: number;
    docKey: string;
    payload: any;
}
export interface ViewChanged {
    firstLoad: boolean;
    currentPage: number;
    numberOfPages: number;
    orientations: { [pageNumber: number]: number };
    pageInfos: PageInfo[];
    selectionRectangles: any[];
    nodeInfos: NodeInfo[];
    selectionText: string;
    clientHeight: number;   // Height of viewer without scrollbars
    clientWidth: number;    // Width of viewer without scrollbars
    centerX: number;
    centerY: number;
    scalePayload: ScalePayload;
    viewerInitiatedPageChange: boolean;
}
export interface PageInfo {
    pageNumber: number;
    scale: number;
    orientation: number;
    top: number;
    left: number;
    width: number;
    height: number;
}
export interface FindResponse {
    currentMatch: number;
    totalMatches: number;
}
export interface FindMatchRectsPayload {
    query: string;          // Raw content-search string; term splitting/matching happens in the viewer (mirrors SearchIndexService semantics).
    pattern?: MatchPattern; // Redaction v2 (#493): find this pattern instead of the query's words. Echoed in MatchRectsResponse.
    keepPlacesApart?: boolean; // Redaction v2 (#493): never join text from two places on the page into one word (pdf.js can list a line's
                            // pieces out of order with no space between). v1 leaves it out and matches as before.
}
// Redaction v2 (#493): what the interactive search finds besides words. 'cpr': a Danish CPR number, ddmmyy-xxxx, with or without the dash.
export type MatchPattern = 'cpr';
// Rects are page-relative, scale 1 (CSS px, 96 DPI), top-down origin, default page
// rotation (no orientation override) — the same space markups are persisted in.
export interface MatchRect {
    left: number;
    top: number;
    width: number;
    height: number;
}
export interface MatchRectsResponse {
    query: string;
    pattern?: MatchPattern; // The request's pattern, echoed; a viewer that predates patterns leaves it out (and searched the query instead)
    matches: { pageNumber: number; rects: MatchRect[]; pageWidth?: number; pageHeight?: number; before?: string; text?: string; after?: string }[];  // One entry per match; multi-line matches carry multiple rects.
                            // pageWidth/pageHeight (#493): the page in the same space, to leave out text outside the visible page.
                            // text (#611): the words the match covers, line breaks as spaces; before/after: up to 30 characters of the page's
                            // text around it, cut at a word. An older viewer leaves all three out.
    pagesTotal: number;
    pagesFailed: number;    // Pages where text extraction threw (-1 = the whole request failed).
}
export interface GetPageRectsPayload {
    pages: number[];        // 1-based page numbers.
}
// Dimensions in stored-markup space: scale 1 (CSS px, 96 DPI), default page
// rotation (no orientation override) — the same space FIND_MATCH_RECTS uses.
export interface PageRect {
    pageNumber: number;
    width: number;
    height: number;
}
export interface PageRectsResponse {
    requested: number[];    // The request's `pages`, echoed verbatim — correlates the response to its request (same role as MatchRectsResponse.query).
    pages: PageRect[];
    pagesFailed: number;    // Pages that were out of range or failed to load (-1 = the whole request failed).
}
export interface GetImageRectsPayload {
    pageNumber: number;     // 1-based.
}
// Rects in stored-markup space (scale 1, CSS px, default rotation), one per image
// paint on the page. failed=true when the operator list could not be walked.
export interface ImageRectsResponse {
    pageNumber: number;
    rects: MatchRect[];
    failed: boolean;
}
export interface ClickEvent {
    x: number;
    y: number;
}
export interface ThumbnailsResponse {
    numberOfPages: number;
    thumbnailURL: string;
    scale: number;
    orientation: number;
    pageNumber: number;
}

export interface QuickViewResponse {
    markupKey: string;
    numberOfPages: number;
    quickViewURL: string;
    orientation: number;
}

// Interface to represent viewport coordinates
export interface ViewportCoordinates {
    top: number;
    left: number;
    bottom: number;
    right: number;
    width: number;
    height: number;
}

// Interface to represent node information
export interface NodeInfo {
    text: string;
    coordinates: ViewportCoordinates;
}

export const DOCAVEA_VIEWERS_IN_TABS_OFFSET: number = 1000; // Offset for the viewers in tabs
